import { Component, OnInit, OnDestroy, ViewChild, ElementRef, TemplateRef, output, EventEmitter, Output, effect } from '@angular/core';
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SettingsService } from '../../../../../../shared/services/settings/settings.service';
import { Subject } from 'rxjs';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ConversationService } from 'src/app/shared/services/conversation/conversation.service';
import { defaultLitmusConversation } from 'src/app/shared/constants/default-litmus-conversation';
import { FeedsService } from 'src/app/shared/services/feeds/feeds.service';
import * as _ from 'lodash'
import { NgIf, NgFor, NgClass } from '@angular/common';
import { CreateNewQuestionComponent } from '../create-new-question/create-new-question.component';

// Interfaces for typing
interface QuestionComponent {
  _id: string;
  name: string;
  display_name?: string;
  category?: string;
  type?: string;
  display_on?: string;
  component_category?: string;
  scale_rater_type?: number;
  main_rater_type?: number;
  icon?: string;
  schema?: any;
}

interface Question {
  field_id: string;
  component_id?: string;
  component_type?: string;
  component_name?: string;
  component_category?: string;
  text?: string;
  question?: string;
  label?: string;
  is_deleted?: boolean;
  group_id?: string;
  readOnly?: boolean;
  is_primary_rating?: boolean;
  is_primary_comment?: boolean;
  is_mandatory?: boolean;
  is_displayed_on_dashboard?: boolean;
  is_displayed_on_summary?: boolean;
  is_displayed_emoticon_on_dashboard?: boolean;
  type?: string;
  conversation_type?: string;
  hide_label?: boolean;
  do_not_repeat?: boolean;
  possible_values?: any[];
  sub_questions?: SubQuestion[];
  params?: any;
  style?: {
    text?: any;
    interaction?: any;
    [key: string]: any;
  };
  scale_rater_type?: number;
  main_rater_type?: number;
  rating_bar_reactor_allowed?: boolean;
  reactor_type?: string;
  [key: string]: any; // Allow for additional properties
}

// Add interface for sub-questions to properly type the is_hidden property
interface SubQuestion {
  field_id: string;
  text: string;
  is_hidden: boolean;
}

@Component({
  selector: 'app-litmus-intraction-questionnaire',
  templateUrl: './litmus-intraction-questionnaire.component.html',
  styleUrls: ['./litmus-intraction-questionnaire.component.scss'],
  imports: [FormsModule, NgIf, CdkDropList, NgFor, CdkDrag, CdkDragHandle, CreateNewQuestionComponent, ReactiveFormsModule, NgClass]
})
export class LitmusIntractionQuestionnaireComponent implements OnInit, OnDestroy {
  // View references with definite assignment assertion
  @ViewChild('questionModal') questionModal!: TemplateRef<any>;
  @ViewChild('componentSchema') componentSchemaRef!: ElementRef;
  @ViewChild('questionInfoModal') questionInfoModal!: TemplateRef<any>;
  @Output() updateQuestionnaire = new EventEmitter<any>();
  @Output() questionModalEvent = new EventEmitter<any>()

  // Data
  questions: Question[] = [];
  filteredQuestions: Question[] = [];
  components: QuestionComponent[] = [];
  filteredComponents: QuestionComponent[] = [];
  componentFilter: string = '';

  // Modal references and state
  currentModal: NgbModalRef | null = null;
  currentQuestion: Question | null = null;
  isEditMode: boolean = false;
  showMultiSelectOptions: boolean = false;

  // Form references
  questionForm!: FormGroup;

  // Component state
  loading: boolean = true;
  projectStatus: number = 1; // 1 = Setup, 2 = Live
  isPrimaryQuestionSet: boolean = false;
  isPrimaryCommentSet: boolean = false;
  isEditInteractionType: boolean = false;
  deviceType: string = 'phone';
  hasOrderChanged: boolean = false; // Track if question order has changed
  originalQuestionOrder: string[] = []; // Store original order for comparison
  isSaving: boolean = false; // Track save operation state
  similarTypeComponents: string[] = [
    'ltms-emoticon-wheel', 'ltms-ten-pill-rater', 'ltms-nps-emoticon-box-rater',
    'ltms-nps-fun-wheel', 'ltms-nps-slider-rater', 'ltms-nps-wheel',
    'ltms-emoticon-rater', 'ltms-five-pill-rater', 'ltms-five-point-slider-rater',
    'ltms-fun-wheel-five-point', 'ltms-star-rater', 'ltms-detailed-slider',
    'ltms-fun-one-wheel', 'ltms-fun-wheel', 'ltms-slider-rater',
    'ltms-wheel', 'ltms-nps-five-star-rater'
  ];

  // Rich text options
  richTextOptions = {
    lang: "en",
    height: 120,
    disableResizeEditor: true,
    dialogsInBody: true,
    fontSizes: ['8', '9', '10', '11', '12', '14', '18', '24', '36'],
    fontNames: ['Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Roboto'],
    fontNamesIgnoreCheck: ['Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Roboto'],
    toolbar: [
      ["fontname", ["fontname"]],
      ['fontsize', ['fontsize']],
      ['style', ['bold', 'italic', 'underline', 'color']],
      ['para', ['paragraph']]
    ]
  };

  private destroy$ = new Subject<void>();
  private _searchText: string = '';

  // Define searchText as property with getter/setter
  get searchText(): string {
    return this._searchText;
  }

  set searchText(value: string) {
    this._searchText = value;
    this.filterQuestions();
  }
  brandDetails: any = null;
  defaultInteraction: any = []
  componentHash: any = {}

  showQuestionModal: boolean = false;

  isUserHasWriteAccess: boolean = false;
  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private settingsService: SettingsService,
    private _stateService: AppStateService,
    private _conversations: ConversationService,
    private _feedsService: FeedsService
  ) {
    effect(() => {
      if (this._stateService.initialized()) {
        const brandDetails: any = this._stateService.brandDetails()
        this.isUserHasWriteAccess = this._conversations.isUserHasWriteAccess('MANAGE_CONVERSATION');
        if (!_.isEmpty(brandDetails)) {
          this.brandDetails = brandDetails
          this.projectStatus = brandDetails?.project_info?.project_status;
          this.initForms();
          this.loadData();
        }
      }
    })
    effect(() => {
      const conversationObject = this._conversations.conversationObject();
      if (conversationObject) {
        this.defaultInteraction = _.reduce(defaultLitmusConversation.fields || [], (p, d) => {
          if (d.label) { p.push(d.label); }
          return p;
        }, []);


        var data = _.reduce(Helpers.deepClone(this._conversations.configuration.fields), (p, d) => {
          if (this.defaultInteraction.indexOf(d.label) !== -1) {
            p.push(d);
          }
          return p;
        }, []);

        this.questions = _.filter(Helpers.deepClone(this._conversations.configuration.fields), (el) => {
          if (this.defaultInteraction.indexOf(el.label) === -1) return el;
        });
        this.questions = this.questions.concat(data);

        // Simulate loading questions from API (in a real app, this would be from an API)
        // this.questions = _.filter(conversationObject.configuration.fields, (field: any) => {
        //   return !(field.group_id)
        // });
        // console.log("this.questions",this.questions)
        // console.log("this.questions",Helpers.deepClone(this.questions))
        this.filteredQuestions = [...this.questions];
        this.loading = false;

        // Initialize original question order for tracking changes
        this.originalQuestionOrder = this.questions.map(q => q.field_id);
        this.hasOrderChanged = false;

        // Check if primary question and comment exist
        this.checkExistingPrimaryQuestions();

        this.loading = false;
        this.filterQuestions();
      }
    })
  }

  ngOnInit(): void {

  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    if (this.currentModal) {
      this.currentModal.close();
    }
  }

  // Initialize forms
  initForms(): void {
    this.questionForm = this.fb.group({
      field_id: [''],
      component_id: ['', Validators.required],
      component_type: [''],
      text: ['', Validators.required],
      is_primary_rating: [false],
      is_primary_comment: [false],
      is_mandatory: [true],
      is_displayed_on_dashboard: [true],
      is_displayed_on_summary: [false],
      hide_label: [false],
      sub_questions: this.fb.array([])
    });

    // Initialize form watchers after form is created
    this.watchFormChanges();
  }

  // Watch form value changes
  watchFormChanges(): void {
    // Watch component ID change
    const componentIdControl = this.questionForm.get('component_id');
    if (componentIdControl) {
      componentIdControl.valueChanges
        .pipe(
          takeUntil(this.destroy$)
        )
        .subscribe(componentId => {
          if (componentId) {
            this.onComponentSelected(componentId);
          }
        });
    }

    // Watch primary rating change
    const primaryRatingControl = this.questionForm.get('is_primary_rating');
    if (primaryRatingControl) {
      primaryRatingControl.valueChanges
        .pipe(
          takeUntil(this.destroy$)
        )
        .subscribe(isPrimary => {
          if (isPrimary) {
            this.onPrimaryRatingChanged(isPrimary);
          }
        });
    }

    // Watch primary comment change
    const primaryCommentControl = this.questionForm.get('is_primary_comment');
    if (primaryCommentControl) {
      primaryCommentControl.valueChanges
        .pipe(
          takeUntil(this.destroy$)
        )
        .subscribe(isPrimary => {
          if (isPrimary) {
            this.onPrimaryCommentChanged(isPrimary);
          }
        });
    }
  }

  // Load components and questions data
  loadData(): void {
    this.loading = true;

    // Use the settings service to load components
    this.settingsService.getQuestionnaireComponents().subscribe(
      (response) => {
        if (response && response?.data?.code == 0 && !_.isEmpty(response.data.components)) {
          this.components = response.data.components;
          this._conversations.createHashForComponent(this.components)
          this.filteredComponents = [...this.components];
          this.componentHash = this._conversations.getComponentHash()
        } else {
          // Fallback to sample data if API call fails
          this.components = this.getQuestionComponents();
          this.filteredComponents = [...this.components];
        }
      },
      (error) => {
        console.error('Error loading components:', error);
        // Fallback to sample data if API call fails
        this.components = this.getQuestionComponents();
        this.filteredComponents = [...this.components];
        // this.getSampleQuestions();
        // this.checkExistingPrimaryQuestions();
        //   this.loading = false;
        // this.filterQuestions();
      }
    );
  }

  // Check if primary question and comment exist
  checkExistingPrimaryQuestions(): void {
    this.isPrimaryQuestionSet = this.questions.some(q => q.is_primary_rating === true);
    this.isPrimaryCommentSet = this.questions.some(q => q.is_primary_comment === true);
  }

  // Filter questions based on search text
  filterQuestions(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.filteredQuestions = [...this.questions];
    } else {
      const searchTerm = this.searchText.toLowerCase().trim();
      this.filteredQuestions = this.questions.filter(q => {
        const questionText = (q.text || q.question || '').toLowerCase();
        return questionText.includes(searchTerm);
      });
    }
  }

  // Filter components based on search text
  filterComponents(value: string): void {
    this.componentFilter = value;

    if (!value || value.trim() === '') {
      this.filteredComponents = [...this.components];
    } else {
      const searchTerm = value.toLowerCase().trim();
      this.filteredComponents = this.components.filter(comp => {
        const displayName = (comp.display_name || comp.name || '').toLowerCase();
        return displayName.includes(searchTerm);
      });
    }
  }

  modalData: any = {
    question: {},
    components: {},
    isEditMode: false,
    isPrimaryQuestionSet: false,
    isPrimaryCommentSet: false,
    projectStatus: 1,
  }
  isQuestionModalOpen: boolean = false;
  isEditeMode: boolean = false;
  createNewQuestion() {
    this.openQuestionModal();
  }


  // Open modal to create or edit a question
  openQuestionModal(question?: Question): void {


    // Configure the modal component
    const modalComponent: any = {};//modalRef.componentInstance;
    modalComponent.components = this.components;
    modalComponent.isEditMode = !!question;
    modalComponent.isPrimaryQuestionSet = this.isPrimaryQuestionSet;
    modalComponent.isPrimaryCommentSet = this.isPrimaryCommentSet;
    modalComponent.projectStatus = this.projectStatus;
    this.isEditMode = false;
    if (question && !_.isEmpty(question)) {
      // Pass a deep clone of the question to avoid direct reference modification
      modalComponent.question = JSON.parse(JSON.stringify(question));
      this.isEditMode = true;
    }
    this.modalData = Helpers.deepClone(modalComponent)
    this.isQuestionModalOpen = true;
    // Subscribe to save event
    // modalComponent.result.then((result:any)=>{
    //   console.log("result  >>>>>", result);
    // });
    // modalComponent.saveEvent.subscribe((savedQuestion: Question) => {
    //   console.log("savedQuestion  >>>>>", savedQuestion);
    //   // if (modalComponent.isEditMode) {
    //   //   // Update existing question
    //   //   this.updateQuestion(savedQuestion);
    //   // } else {
    //   //   // Add new question
    //   //   this.addQuestion(savedQuestion);
    //   // }

    //   // Update primary question flags
    //   // this.checkExistingPrimaryQuestions();

    //   // // Refresh filtered questions
    //   // this.filterQuestions();
    // });

    // Store the modal reference
    // this.currentModal = modalRef;
  }
  saveEvent(savedQuestion: any) {
    // console.log("savedQuestion  >>>>>", savedQuestion);
    this.isQuestionModalOpen = false;
  }
  // Close the current modal
  closeModal(): void {
    this.isQuestionModalOpen = false
    // if (this.currentModal) {
    //   this.currentModal.close();
    // }
  }

  // Show information about a question
  showQuestionInfo(question: Question): void {
    this.currentQuestion = question;
    this.currentModal = this.modalService.open(this.questionInfoModal, { size: 'md' });
  }

  // Reset the question form
  resetQuestionForm(): void {
    this.questionForm.reset({
      is_mandatory: true,
      is_displayed_on_dashboard: true,
      is_displayed_on_summary: false,
      hide_label: false
    });

    this.getSubQuestionsFormArray().clear();
    this.isEditInteractionType = false;
    this.filteredComponents = [...this.components];
  }

  // Populate form with question data
  populateQuestionForm(question: Question): void {
    this.questionForm.patchValue({
      field_id: question.field_id,
      component_id: question.component_id,
      component_type: question.component_type,
      text: question.text || question.question || '',
      is_primary_rating: question.is_primary_rating || false,
      is_primary_comment: question.is_primary_comment || false,
      is_mandatory: question.is_mandatory !== undefined ? question.is_mandatory : true,
      is_displayed_on_dashboard: question.is_displayed_on_dashboard !== undefined ? question.is_displayed_on_dashboard : true,
      is_displayed_on_summary: question.is_displayed_on_summary || false,
      hide_label: question.hide_label || false
    });

    // Add sub-questions if they exist
    if (question.sub_questions && question.sub_questions.length > 0) {
      const subQuestionsArray = this.getSubQuestionsFormArray();

      question.sub_questions.forEach(subQ => {
        subQuestionsArray.push(this.createSubQuestionControl(subQ));
      });

      this.showMultiSelectOptions = true;
    }
  }

  // Create an empty question object
  createEmptyQuestion(): Question {
    const labelId = 'label_' + Math.random().toString(36).substring(2, 9);
    return {
      field_id: 'question_' + Math.random().toString(36).substring(2, 9),
      text: '',
      component_type: undefined,
      component_id: undefined,
      is_primary_rating: false,
      is_mandatory: true,
      is_displayed_on_dashboard: true,
      is_displayed_on_summary: false,
      conversation_type: 'question',
      label: labelId,
      possible_values: [],
      params: {},
      hide_label: false,
      do_not_repeat: false,
      style: {
        text: {},
        interaction: {}
      }
    };
  }

  // Get the sub-questions form array
  getSubQuestionsFormArray(): FormArray {
    return this.questionForm.get('sub_questions') as FormArray;
  }

  // Getter for subQuestions to be used in template
  get subQuestions(): FormArray {
    return this.getSubQuestionsFormArray();
  }

  // Create a form control for a sub-question
  createSubQuestionControl(subQuestion?: any): FormGroup {
    return this.fb.group({
      field_id: [subQuestion ? subQuestion.field_id : 'sub_' + Math.random().toString(36).substring(2, 9)],
      text: [subQuestion ? subQuestion.text : '', Validators.required],
      is_hidden: [subQuestion ? subQuestion.is_hidden : false]
    });
  }

  // Add a new sub-question
  addSubQuestion(): void {
    this.getSubQuestionsFormArray().push(this.createSubQuestionControl());
  }

  // Remove a sub-question
  removeSubQuestion(index: number): void {
    this.getSubQuestionsFormArray().removeAt(index);
  }

  // Handle component selection
  onComponentSelected(componentId: string): void {
    const component = this.getComponentById(componentId);

    if (!component) return;

    // Set component type on form
    this.questionForm.patchValue({
      component_type: component.name
    });

    // Show multi-select options if applicable
    this.showMultiSelectOptions = component.component_category === 'multi-select';

    if (component.component_category === 'multi-select' && this.getSubQuestionsFormArray().length === 0) {
      // Add initial options for multi-select
      this.addSubQuestion();
      this.addSubQuestion();
    }

    // Draw component schema if available
    if (component.schema && this.componentSchemaRef?.nativeElement) {
      this.drawComponentSchema(component.schema);
    }
  }

  // Handle primary rating checkbox change
  onPrimaryRatingChanged(isPrimary: boolean): void {
    if (isPrimary && this.isPrimaryQuestionSet && !this.isEditMode) {
      // Show warning that another primary rating exists
      const confirmed = confirm('Another primary rating question already exists. Do you want to replace it?');

      if (!confirmed) {
        this.questionForm.patchValue({
          is_primary_rating: false
        });
        return;
      }

      // If confirmed, we'll update the existing primary question later when saving
    }

    // If this is set as primary rating, ensure primary comment is not set
    if (isPrimary) {
      this.questionForm.patchValue({
        is_primary_comment: false
      });
    }
  }

  // Handle primary comment checkbox change
  onPrimaryCommentChanged(isPrimary: boolean): void {
    if (isPrimary && this.isPrimaryCommentSet && !this.isEditMode) {
      // Show warning that another primary comment exists
      const confirmed = confirm('Another primary comment question already exists. Do you want to replace it?');

      if (!confirmed) {
        this.questionForm.patchValue({
          is_primary_comment: false
        });
        return;
      }

      // If confirmed, we'll update the existing primary question later when saving
    }

    // If this is set as primary comment, ensure primary rating is not set
    if (isPrimary) {
      this.questionForm.patchValue({
        is_primary_rating: false
      });
    }
  }

  // Save the question
  saveQuestion(): void {
    if (this.questionForm.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.questionForm.controls).forEach(key => {
        const control = this.questionForm.get(key);
        if (control) control.markAsTouched();
      });

      // For multi-select, check if all options are valid
      if (this.showMultiSelectOptions) {
        const subQuestionsArray = this.getSubQuestionsFormArray();

        if (subQuestionsArray.length < 2) {
          alert('Multi-select questions must have at least 2 options.');
          return;
        }

        for (let i = 0; i < subQuestionsArray.length; i++) {
          const subQuestion = subQuestionsArray.at(i) as FormGroup;
          Object.keys(subQuestion.controls).forEach(key => {
            const control = subQuestion.get(key);
            if (control) control.markAsTouched();
          });
        }
      }

      return;
    }

    // Get form values
    const formValues = this.questionForm.value;

    // Handle primary question replacement if needed
    if (formValues.is_primary_rating && this.isPrimaryQuestionSet && !this.isEditMode) {
      this.resetExistingPrimaryRating();
    }

    // Handle primary comment replacement if needed
    if (formValues.is_primary_comment && this.isPrimaryCommentSet && !this.isEditMode) {
      this.resetExistingPrimaryComment();
    }

    // Create or update question
    const componentId = formValues.component_id;
    const component = componentId ? this.getComponentById(componentId) : undefined;

    const savedQuestion: Question = {
      ...this.currentQuestion,
      ...formValues,
      component_name: component?.name || ''
    };

    if (this.isEditMode) {
      // Update existing question
      const index = this.questions.findIndex(q => q.field_id === savedQuestion.field_id);
      if (index !== -1) {
        this.questions[index] = savedQuestion;
      }
    } else {
      // Add new question
      this.questions.push(savedQuestion);
    }

    // Update primary question flags
    this.checkExistingPrimaryQuestions();

    // Refresh filtered questions
    this.filterQuestions();

    // Close modal
    this.closeModal();
  }

  // Reset existing primary rating question
  resetExistingPrimaryRating(): void {
    const primaryIndex = this.questions.findIndex(q => q.is_primary_rating === true);
    if (primaryIndex !== -1) {
      this.questions[primaryIndex] = {
        ...this.questions[primaryIndex],
        is_primary_rating: false
      };
    }
  }

  // Reset existing primary comment question
  resetExistingPrimaryComment(): void {
    const primaryIndex = this.questions.findIndex(q => q.is_primary_comment === true);
    if (primaryIndex !== -1) {
      this.questions[primaryIndex] = {
        ...this.questions[primaryIndex],
        is_primary_comment: false
      };
    }
  }

  // Handle drag and drop reordering
  onDrop(event: CdkDragDrop<Question[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.filteredQuestions, event.previousIndex, event.currentIndex);

      // Find corresponding indices in the original questions array
      const itemId = this.filteredQuestions[event.currentIndex].field_id;
      const originalIndex = this.questions.findIndex(q => q.field_id === itemId);
      const prevItemId = this.filteredQuestions[event.previousIndex]?.field_id;
      const prevOriginalIndex = prevItemId ? this.questions.findIndex(q => q.field_id === prevItemId) : -1;

      if (originalIndex !== -1 && prevOriginalIndex !== -1) {
        moveItemInArray(this.questions, prevOriginalIndex, originalIndex);
      }

      // Check if order has changed by comparing with original order
      const currentOrder = this.questions.map(q => q.field_id);
      if (!this.arraysEqual(currentOrder, this.originalQuestionOrder)) {
        this.hasOrderChanged = true;
      }

      // Here you would typically call an API to update the order in the backend
      // console.log('Questions reordered:', this.questions);
    }
  }

  // Helper method to compare arrays
  private arraysEqual(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  // Get the appropriate icon for the question type
  getQuestionTypeIcon(question: any): string {
    // if (question.is_primary_rating) {
    //   return 'assets/images/rating.svg';
    // }

    // if (question.is_primary_comment) {
    //   return 'assets/images/comments.svg';
    // }



    // For other component types
    let componentName = question.component_type || 'question';
    if (question?.component_id && this.componentHash[question?.component_id] && this.componentHash[question?.component_id].name) {
      componentName = this.componentHash[question?.component_id].name;
    }
    return `https://static1.litmusworld.com/interaction_icons/${componentName.toLowerCase()}.svg`;
  }

  // Check if a question can be deleted (based on project status)
  canDeleteQuestion(question: any): boolean {
    // In this example, we can only delete questions if the project is in Setup status

    return (this.projectStatus === 1 || this.projectStatus === 5) && this.isUserHasWriteAccess;
  }

  // Check if a question type is unsupported for copying
  isUnsupportedForCopy(question: any): boolean {
    return question.component_type === 'ltms-multi-check-box' ||
      question.question_component_type === 'ltms-multi-check-box' ||
      question.component_type === 'ltms-single-select-options';
  }

  // Create a copy of the question
  copyQuestion(question: Question): void {
    const copiedQuestion: Question = {
      ...JSON.parse(JSON.stringify(question)),
      field_id: 'question_' + Math.random().toString(36).substring(2, 9),
      is_primary_rating: false,
      is_primary_comment: false,
      text: `Copy of ${question.text || question.question}`
    };

    // If the question has sub-questions, create copies of those too
    if (question.sub_questions && question.sub_questions.length > 0) {
      copiedQuestion.sub_questions = question.sub_questions.map(subQ => ({
        ...subQ,
        field_id: 'sub_' + Math.random().toString(36).substring(2, 9)
      }));
    }

    this.questions.push(copiedQuestion);
    this.filterQuestions();
  }

  // Edit an existing question
  editQuestion(question: Question): void {
    this.openQuestionModal(question);
  }

  // Delete a question
  deleteQuestion(question: Question): void {
    if (!this.isUserHasWriteAccess) {
      Helpers.smallAlertBox('Access Denied', 'You do not have write permission to delete question. Only read access is available.', 'warning', 4000);
      return;
    }
    this.removeQuestion(question.field_id);
    // if (confirm(`Are you sure you want to delete this question: "${question.text || question.question}"?`)) {
    //   // Check if this is a primary question
    //   if (question.is_primary_rating || question.is_primary_comment) {
    //     alert('This is a primary question. Deleting it may affect your reports and dashboards.');
    //   }

    //   // Remove from arrays (in a real app, you'd call an API)
    //   this.questions = this.questions.filter(q => q.field_id !== question.field_id);

    //   // Update primary question flags
    //   this.checkExistingPrimaryQuestions();

    //   // Re-filter to update the view
    //   this.filterQuestions();
    // }
  }


  removeQuestion(fieldId: string) {
    // Primary question can only be deleted if there are no responses in a project.
    if (fieldId == 'primary_rating') {
      // Checking feed count
      var params = {
        "accountId": _.get(this.brandDetails, "account_id") || _.get(Helpers, "brandDetails.account_id"),
        "brandId": _.get(this.brandDetails, "_id") || _.get(Helpers, "brandDetails._id"),
        "limit": 1
      }
      this._feedsService.getFeedsByFilter(params).subscribe((response: any) => {
        if (response) {
          // Prevent deletion if there is response (total_feeds > 0) in project.
          if (response.code == 0 && response.total_feeds > 0) {
            this.preventDeletionForPrimaryRatingQuestion();
          } else if ((response.code == 0 && response.total_feeds == 0) || (response.code == 1 && response.is_index_exists == false)) {
            this.processRemoveQuestion(fieldId)
          } else {
            Helpers.smallAlertBox("Error", "Something went wrong. Please try later.", "error");
          }
        } else {
          Helpers.smallAlertBox("Error", "Something went wrong. Please try later.", "error");
        }
      })
    } else {
      this.processRemoveQuestion(fieldId)
    }
  }

  preventDeletionForPrimaryRatingQuestion() {
    var errorMsg = "Primary question can only be deleted if there are no responses in a project. To continue, please ensure that all response data is cleared from the project as a prerequisite.";
    var title = "Delete Interaction <i class='fa fa-exclamation-triangle' aria-hidden='true' style='color:yellow'></i>";
    var buttons = [{
      text: "I understand",
      type: 'primary',
      key: 'I understand'
    }];
    Helpers.SmartMessageBox({
      title: title,
      content: errorMsg,
      buttons: buttons
    });
  }
  processRemoveQuestion(fieldId) {
    var isConditionApplied = false;
    var errorMsg = "Are you sure you want to delete this interaction?";
    var title = "Delete Interaction";
    var buttons = [{
      text: "No",
      type: 'secondary',
      key: 'No'
    }, {
      text: "Yes",
      type: 'danger',
      key: 'Yes'
    }];
    var fieldObj = this._conversations.fieldsHash[fieldId];
    // if (fieldObj) {
    //     if (fieldObj.is_mandatory_if && (fieldObj.is_mandatory_if.length > 1)) {
    //         isConditionApplied = true;
    //         if (fieldObj.allowMultiSelect && fieldObj.interaction_type && fieldObj.interaction_type == "multi-select") {
    //             isConditionApplied = false;
    //             var options = _.filter(cnvdr.configuration.fields, function (field) {
    //                 if (field.group_id && field.group_id == fieldId) {
    //                     return false;
    //                 } else {
    //                     if (field.field_id != fieldId) {
    //                         return true;
    //                     } 
    //                     return false;
    //                 }
    //             });
    //             for (var index = 0; index < options.length; index++) {
    //                 if (fieldObj.is_mandatory_if.indexOf(options[index].field_id) > -1) {
    //                     isConditionApplied = true;
    //                     break;
    //                 }                                 
    //             }                            
    //         }
    // _.each(cnvdr.configuration.fields, function (convoField) {
    //     if (convoField.is_mandatory_if && (convoField.is_mandatory_if.indexOf(fieldObj.field_id) > -1)) {
    //         if (convoField.group_id && fieldObj.group_id && convoField.group_id != fieldObj.group_id) {
    //             isConditionApplied = true;
    //             return;
    //         }
    //     }
    // });
    // } 
    // if (fieldObj['visible-if'] && (fieldObj['visible-if'].length > 1)) {
    //     isConditionApplied = true;
    // } 
    // if (fieldObj.allowMultiSelect && fieldObj.interaction_type && fieldObj.interaction_type == "multi-select") {
    //     var options = _.filter(cnvdr.configuration.fields, function (field) {
    //         if (field.group_id && field.group_id == fieldId) {
    //             return true;
    //         } else {
    //             return false;
    //         }
    //     });
    //     for (var index = 0; index < options.length; index++) {
    //         if (options['visible-if'] && options['visible-if'].length > -1) {
    //             isConditionApplied = true;
    //             break;
    //         }                                 
    //     }   
    // } 
    // if (fieldObj['server-visible-if'] && (fieldObj['server-visible-if'].length > 1)) {
    //     isConditionApplied = true;
    // }
    //     if (isConditionApplied) {
    //         errorMsg = "There are conditions/escalations associated with this interaction.";
    //         title = "Warning";
    //         buttons = '[Okay]';
    //     }
    // }
    if (!isConditionApplied) {
      _.each(this._conversations.configuration.fields, (field) => {
        if (field.field_id != fieldId) {
          if (field.is_mandatory_if && (field.is_mandatory_if.length > 1)) {
            if (field.is_mandatory_if.search(fieldId) > 0) isConditionApplied = true;
          }
          if (field['visible-if'] && (field['visible-if'].length > 1)) {
            if (field['visible-if'].search(fieldId) > 0) isConditionApplied = true;
          }
          // if (field['server-visible-if'] && (field['server-visible-if'].length > 1)) {
          //     if (field['server-visible-if'].search(fieldId) > 0) isConditionApplied = true;
          // }
          if (fieldObj.allowMultiSelect && fieldObj.interaction_type && fieldObj.interaction_type == "multi-select") {
            var options = _.filter(this._conversations.configuration.fields, function (field) {
              if (field.group_id && field.group_id == fieldId) {
                return true;
              } else {
                return false;
              }
            });
            for (var index = 0; index < options.length; index++) {
              if (field['visible-if'] && field['visible-if'].indexOf(options[index].field_id) > -1) {
                isConditionApplied = true;
                break;
              }
              if (field.group_id) {
                if ((field.group_id != fieldId) && (field.is_mandatory_if && field.is_mandatory_if.indexOf(options[index].field_id) > -1)) {
                  isConditionApplied = true;
                  break;
                }
              } else {
                if (field.is_mandatory_if && field.is_mandatory_if.indexOf(options[index].field_id) > -1) {
                  isConditionApplied = true;
                  break
                }
              }
            }
          }
          if (isConditionApplied) {
            errorMsg = "This is a conditional interaction of another interaction.";
            title = "Warning";
            buttons = [{
              text: "Okay",
              type: 'primary',
              key: 'Okay'
            }];
          }
        }
      });
    }
    Helpers.SmartMessageBox({
      title: title,
      content: errorMsg,
      buttons: buttons
    }, (ButtonPressed) => {
      if (ButtonPressed === "Yes") {
        var element: any = document.getElementById('field_' + fieldId);
        if (element) {
          element.style.pointerEvents = 'none';
          element.style.cursor = 'not-allowed';
          element.style.backgroundColor = '#ddd';
        }
        this.executeRemoveQuestion(fieldId);
      }
    });
  }

  executeRemoveQuestion(fieldId) {
    this.isSaving = true;
    var interaction = this._conversations.fieldsHash[fieldId];
    if (interaction) {
      interaction.is_deleted = true;
      // Bug Fix - Added by Bhunesh - Removing sub questions if parent question is being deleted
      this._conversations.configuration.fields = this._conversations.configuration.fields.map(function (field) {
        if (field && field.group_id && field.group_id == fieldId) {
          field.is_deleted = true;
        }
        return field;
      })
    }
    if (this._conversations.configuration.screens) {
      _.each(this._conversations.devices, (deviceId) => {
        if (this._conversations.configuration.screens[deviceId]) {
          var pages = this._conversations.configuration.screens[deviceId][this._conversations.default_language.toString()];
          if (pages && pages.length > 0) {
            _.each(pages, (page) => {
              page.fields = _.filter(page.fields, (field) => {
                if (field && field.field_id) {
                  return field.field_id != fieldId;
                } else {
                  return false;
                }
              })
            })
          }
        }
      })
    }
    this._conversations.saveConversation(false, "Interaction Removed").then((response) => {
      // this.showEditModal = false;
      this.isSaving = false;
    })
  }

  // Helper method to get a component by ID
  getComponentById(id: string): QuestionComponent | undefined {
    return this.components.find(c => c._id === id);
  }

  // Draw component schema (placeholder for actual implementation)
  drawComponentSchema(schema: any): void {
    if (!this.componentSchemaRef?.nativeElement) return;

    // In a real app, this would render the component preview
    const element = this.componentSchemaRef.nativeElement;
    element.innerHTML = `<div class="component-preview p-3 bg-light rounded">
      <h6>Component Preview</h6>
      <p class="text-muted small">Preview would be rendered here based on the schema</p>
    </div>`;
  }

  // Get sample question components (simulate API response)
  getQuestionComponents(): QuestionComponent[] {
    return [
      {
        _id: 'rating_5_star',
        name: 'ltms-star-rater',
        display_name: '5-Star Rating',
        category: 'rating',
        type: 'rating',
        display_on: 'studio',
        component_category: 'single-select',
        scale_rater_type: 5,
        main_rater_type: 5,
        icon: 'assets/images/star-rating.svg'
      },
      {
        _id: 'rating_nps',
        name: 'ltms-nps-slider-rater',
        display_name: 'NPS Rating (0-10)',
        category: 'rating',
        type: 'rating',
        display_on: 'studio',
        component_category: 'single-select',
        scale_rater_type: 11,
        main_rater_type: 11,
        icon: 'assets/images/nps-rating.svg'
      },
      {
        _id: 'rating_emoji',
        name: 'ltms-emoticon-rater',
        display_name: 'Emoji Rating',
        category: 'rating',
        type: 'rating',
        display_on: 'studio',
        component_category: 'single-select',
        scale_rater_type: 5,
        main_rater_type: 5,
        icon: 'assets/images/emoji-rating.svg'
      },
      {
        _id: 'text_comment',
        name: 'ltms-text-area',
        display_name: 'Comment Box',
        category: 'comment',
        type: 'text',
        display_on: 'studio',
        component_category: 'text',
        icon: 'assets/images/comment.svg'
      },
      {
        _id: 'multi_select',
        name: 'ltms-multi-check-box',
        display_name: 'Multiple Choice (Checkboxes)',
        category: 'choice',
        type: 'multi-select',
        display_on: 'studio',
        component_category: 'multi-select',
        icon: 'assets/images/checkbox.svg'
      },
      {
        _id: 'single_select',
        name: 'ltms-single-select-options',
        display_name: 'Single Choice (Radio)',
        category: 'choice',
        type: 'single-select',
        display_on: 'studio',
        component_category: 'single-select',
        icon: 'assets/images/radio.svg'
      }
    ];
  }

  // Get sample questions data
  getSampleQuestions() {

    this.settingsService.getFeedbackConfiguration(this.brandDetails._id).subscribe((res: any) => {
      // console.log(">>>>>>>>>>>>>>>>>>>>>>>", res);
      if (res && res.data.code === 0 && res.data.brand_configuration && res.data.brand_configuration.length > 0) {
        this.questions = _.filter(res.data.brand_configuration[0].fields, (field: any) => {
          return !(field.group_id)
        });
        this.filteredQuestions = [...this.questions];
        this.loading = false;
      }
    });
  }

  // Add a new question
  addQuestion(question: Question): void {
    // Handle primary question replacement if needed
    if (question.is_primary_rating && this.isPrimaryQuestionSet) {
      this.resetExistingPrimaryRating();
    }

    if (question.is_primary_comment && this.isPrimaryCommentSet) {
      this.resetExistingPrimaryComment();
    }

    // Add the question to the array
    this.questions.push(question);

    // Mark that changes have been made
    this.hasOrderChanged = true;

    // Check if we need to scroll the view to show the new question
    setTimeout(() => {
      // Scroll to the newly added question
      const element = document.querySelector('.question-card:last-child');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  // Update an existing question
  updateQuestion(question: Question): void {
    // Handle primary question replacement if needed
    if (question.is_primary_rating && this.isPrimaryQuestionSet) {
      const currentPrimary = this.questions.find(q => q.is_primary_rating && q.field_id !== question.field_id);
      if (currentPrimary) {
        currentPrimary.is_primary_rating = false;
      }
    }

    if (question.is_primary_comment && this.isPrimaryCommentSet) {
      const currentPrimaryComment = this.questions.find(q => q.is_primary_comment && q.field_id !== question.field_id);
      if (currentPrimaryComment) {
        currentPrimaryComment.is_primary_comment = false;
      }
    }

    // Find and update the question
    const index = this.questions.findIndex(q => q.field_id === question.field_id);
    if (index !== -1) {
      this.questions[index] = question;
      // Mark that changes have been made
      this.hasOrderChanged = true;
    }
  }



  copyInteraction(interaction) {
    if (!this.isUserHasWriteAccess) {
      Helpers.smallAlertBox('Access Denied', 'You do not have write permission to clone question. Only read access is available.', 'warning', 4000);
      return;
    }
    this.isSaving = true;
    var tmp = Helpers.deepClone(interaction);
    tmp.field_id = this._conversations.generateId('question');
    tmp.text = "Copy " + tmp.text;
    tmp.question = tmp.text;
    tmp.type = 'detailed-rating';
    if (tmp.conversation_type == 'question') {
      tmp['read-only'] = false;
    } else {
      tmp['read-only'] = true;
    }
    this._conversations.props_to_delete.forEach(function (prop) {
      delete tmp[prop];
    })

    tmp.label = this._conversations.generateId('label') + Helpers.getRandomString();

    this._conversations.setLabelById(this._conversations.default_language, tmp.label, tmp.text);

    this._conversations.configuration.fields.push(tmp);

    this._conversations.fieldsHash[tmp.field_id] = tmp;


    if (interaction.component_catagory === 'multi-select' || interaction.question_component_type == 'ltms-multi-check-box') {
      var children = Helpers.deepClone(this._conversations.getFieldsByGroupId(interaction.field_id));
      if (children && children.length > 0) {
        children = children.map((child) => {
          child.group_id = tmp.field_id;
          child.field_id = this._conversations.generateId('question') + Helpers.getRandomString();
          child.labelId = this._conversations.generateId('label') + Helpers.getRandomString();
          return child;
        });
      }
      this._conversations.configuration.fields.concat(children);
    }

    // Mark that changes have been made
    this.hasOrderChanged = true;

    this._conversations.saveConversation(false, "Interaction Copied").then((response) => {
      // this.showEditModal = false;
      this.isSaving = false;
    })

  }

  // Save all questions (placeholder for actual save logic)
  saveQuestions() {
    if (!this.hasOrderChanged || this.isSaving) {
      // console.log('No changes to save or already saving');
      return;
    }

    this.isSaving = true;

    // Update the conversation configuration with the new question order
    if (this._conversations.configuration && this._conversations.configuration.fields) {
      // Create a map of field_id to field object for quick lookup
      const fieldMap = new Map();
      this._conversations.configuration.fields.forEach(field => {
        fieldMap.set(field.field_id, field);
      });

      // Reorder the fields based on the new question order
      const reorderedFields: any[] = [];

      // Add questions in the new order
      this.questions.forEach(question => {
        const field = fieldMap.get(question.field_id);
        if (field) {
          reorderedFields.push(field);
        }
      });

      // Add any remaining fields that might not be in the questions array
      this._conversations.configuration.fields.forEach(field => {
        if (!reorderedFields.find(f => f.field_id === field.field_id)) {
          reorderedFields.push(field);
        }
      });

      // Update the configuration
      this._conversations.configuration.fields = reorderedFields;

      // Save the conversation configuration
      this._conversations.saveConversation(false, "Question Order Updated")
        .then(() => {
          // Reset the change flag and update original order
          this.hasOrderChanged = false;
          this.originalQuestionOrder = this.questions.map(q => q.field_id);
          this.isSaving = false;

          // Show success message
          // console.log('Question order saved successfully');
          // You can add a toast notification here if you have a notification service
        })
        .catch((error) => {
          // console.error('Error saving question order:', error);
          this.isSaving = false;
          // You can add an error notification here
        });
    }
  }


}
